import React, { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { useSeo } from "@/lib/useSeo";
import { TrustBadge } from "@/components/public/sections";
import { CoverageChecker } from "@/components/public/CoverageChecker";
import { NearbyTerritories } from "@/components/public/NearbyTerritories";
import { CalloutBanner, LocalTeam, LocalPartners, LocalResources } from "@/components/public/LocalContentBlocks";
import { IntakeForm } from "@/components/public/IntakeForm";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Phone, MessageCircle, CalendarHeart, Check, ArrowRight, Loader2, HelpCircle,
  Users, Handshake, MapPin, HeartHandshake, Sparkles, ListChecks, Clock,
} from "lucide-react";

const CONTAINER = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (p) => `tel:${(p || "").replace(/[^0-9+]/g, "")}`;

const Bullets = ({ items, icon: Icon = Check, tone = "#2F7D5A" }) => (
  <ul className="space-y-3">
    {(items || []).map((t, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${tone}1f`, color: tone }}><Icon size={15} /></span>
        <span className="text-[17px] text-[#252221] leading-relaxed">{t}</span>
      </li>
    ))}
  </ul>
);

const Block = ({ id, title, eyebrow, children, bg }) => (
  <section id={id} className={`py-12 sm:py-16 ${bg || ""}`}>
    <div className={CONTAINER}>
      {eyebrow && <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">{eyebrow}</p>}
      {title && <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">{title}</h2>}
      <div className="mt-6">{children}</div>
    </div>
  </section>
);

export default function LocalServiceLanePage() {
  const { country, state, city, lane } = useParams();
  const { openChat } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setNotFound(false);
    try {
      const { data } = await publicApi.get(`/public/local/${country}/${state}/${city}/${lane}`);
      setData(data);
      window.scrollTo({ top: 0 });
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  }, [country, state, city, lane]);

  useEffect(() => { load(); }, [load]);

  useSeo({
    title: data?.seo?.title, description: data?.seo?.description,
    canonical: data?.canonical, noindex: notFound || data?.noindex, jsonLd: data?.json_ld,
  });

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (notFound || !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-testid="local-lane-not-found">
        <h1 className="text-3xl font-display font-bold text-[#1C4358]">We couldn't find that page.</h1>
        <p className="mt-3 text-[#4B5563]">Let's get you back on track.</p>
        <Link to="/" className="mmm-tap mt-6 inline-flex items-center gap-2 px-6 h-12 rounded-full text-white bg-[#9C1E22] font-semibold">Back home <ArrowRight size={16} /></Link>
      </div>
    );
  }

  const l = data.lane;
  const local = data.local;
  const { territory } = data;
  const phone = local.phone;
  const label = territory.label;

  return (
    <div data-testid="local-lane-page" data-lane={lane}>
      {/* Local context bar */}
      <div className="bg-[#1C4358] text-white" data-testid="local-context-bar">
        <div className={`${CONTAINER} py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-[13.5px]`}>
          <span className="inline-flex items-center gap-1.5 font-semibold"><MapPin size={14} /> Serving {label}</span>
          {phone && <a href={telHref(phone)} data-testid="local-lane-phone" className="inline-flex items-center gap-1.5 hover:text-[#E0A526]"><Phone size={14} /> {phone}</a>}
          {local.service_availability && <span className="inline-flex items-center gap-1.5 text-white/85"><Clock size={14} /> {local.service_availability}</span>}
        </div>
      </div>

      {/* Hero */}
      <section className="relative isolate overflow-hidden" data-testid="local-lane-hero">
        {l.hero_image_url && <img src={l.hero_image_url} alt={l.name} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 mmm-hero-overlay-side" />
        <div className={`${CONTAINER} relative py-20 sm:py-28`}>
          <div className="max-w-2xl mmm-fade-up">
            <p className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-[13px] font-semibold text-white">
              <MapPin size={14} className="text-[#DEE029]" /> {l.name} — {label}
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl font-display font-bold tracking-tight text-white leading-[1.1]">{l.hero_headline}</h1>
            <p className="mt-5 text-lg sm:text-xl text-white/90 leading-relaxed">{l.hero_subheadline}</p>
            <TrustBadge lane={l.slug} franchise={data.franchise} className="mt-6" />
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <a href="#local-lane-form" data-testid="local-lane-book-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors shadow-lg shadow-black/20">
                <CalendarHeart size={20} /> {l.cta_primary_label || "Book a Free Consultation"}
              </a>
              {phone && (
                <a href={telHref(phone)} data-testid="local-lane-call-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-white hover:bg-white/90 transition-colors">
                  <Phone size={19} /> Call {phone}
                </a>
              )}
              <button type="button" onClick={() => openChat && openChat({ cta_source: "Local Lane Chat", matched_service_lane: l.slug, franchise: data.franchise })} data-testid="local-lane-chat-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-white border-2 border-white/70 hover:bg-white/10 transition-colors">
                <MessageCircle size={19} /> {l.cta_chat_label || "Start Live Chat"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {l.intro_copy && (
        <div className="bg-[#FBF6EC]"><div className={`${CONTAINER} py-10`}>
          <p className="text-xl sm:text-2xl font-display text-[#1C4358] leading-relaxed max-w-4xl">{l.intro_copy}</p>
        </div></div>
      )}

      <CalloutBanner text={l.local_lane_note} />

      <Block eyebrow="Who this is for" title="You're in the right place if…">
        <div className="grid gap-10 md:grid-cols-2">
          <Bullets items={l.who_for} icon={Users} tone="#1C4358" />
          <div>
            <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-3">Common situations</h3>
            <Bullets items={l.common_situations} icon={ListChecks} tone="#9C1E22" />
          </div>
        </div>
      </Block>

      <Block eyebrow="How we help" title={`What we help with in ${territory.city}`} bg="bg-[#9BB9CE]/15">
        <div className="grid gap-4 sm:grid-cols-2">
          {(l.we_help_with || []).map((t, i) => (
            <div key={i} className="flex items-start gap-3 rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
              <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2F7D5A]/12 text-[#2F7D5A]"><Check size={17} /></span>
              <span className="text-[17px] text-[#252221] leading-relaxed">{t}</span>
            </div>
          ))}
        </div>
        {l.opportunity_copy && (
          <div className="mt-8 rounded-[1rem] bg-[#1C4358] mmm-grain text-white p-6 sm:p-8 flex items-start gap-4">
            <HeartHandshake size={28} className="text-[#DEE029] shrink-0" />
            <p className="text-[17px] leading-relaxed">{l.opportunity_copy}</p>
          </div>
        )}
      </Block>

      {(l.questions_first?.length > 0 || l.partners_involved?.length > 0) && (
        <Block eyebrow="No pressure, ever" title="What we ask first">
          <div className="grid gap-10 md:grid-cols-2">
            <Bullets items={l.questions_first} icon={HelpCircle} tone="#1C4358" />
            <div>
              <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-3">Trusted help that may be involved</h3>
              <Bullets items={l.partners_involved} icon={Handshake} tone="#9C1E22" />
            </div>
          </div>
        </Block>
      )}

      {/* Local service area */}
      {(local.mapped_area || local.service_area_note) && (
        <Block eyebrow="In your area" title={`Local support — ${local.name || territory.city}`} bg="bg-[#C4C89B]/25">
          {local.mapped_area && <p className="flex items-center gap-2 text-[17px] text-[#1C4358] font-medium"><MapPin size={18} className="text-[#9C1E22]" /> {local.mapped_area}</p>}
          {local.service_area_note && <p className="mt-3 text-[16px] text-[#4B5563] leading-relaxed">{local.service_area_note}</p>}
        </Block>
      )}

      <CoverageChecker franchise={data.franchise} cityId={local.city_id} label={label} />

      {l.faqs?.length > 0 && (
        <Block eyebrow="Common questions" title="Answers for families">
          <Accordion type="single" collapsible className="max-w-3xl" data-testid="local-lane-faqs">
            {l.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#1C4358]/12">
                <AccordionTrigger className="text-left text-[18px] font-display font-semibold text-[#1C4358] hover:no-underline">{f.q || f.question}</AccordionTrigger>
                <AccordionContent className="text-[16px] text-[#4B5563] leading-relaxed">{f.a || f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Block>
      )}

      {/* Lead form with local attribution */}
      <LocalTeam team={local.team_display} />
      <LocalPartners partners={local.partner_display} />
      <LocalResources links={local.resource_links} />
      <NearbyTerritories nearby={data.nearby} heading={`${l.name} — Nearby Areas`} />

      <section id="local-lane-form" className="py-14 sm:py-20 scroll-mt-20" data-testid="local-lane-form-section">
        <div className={CONTAINER}>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-5">
              <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">Free 1-hour consultation</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#1C4358] leading-tight">You don't have to figure this out alone.</h2>
              <p className="mt-4 text-lg text-[#4B5563] leading-relaxed">Tell us what's happening in {label}. A caring local guide will reach out — no cost, no pressure.</p>
              {phone && (
                <a href={telHref(phone)} className="mmm-tap mt-6 inline-flex items-center gap-2 px-6 h-13 py-3 rounded-full text-[16px] font-semibold text-[#1C4358] border border-[#1C4358]/25 hover:bg-[#1C4358]/6 transition-colors">
                  <Phone size={18} /> Or call {phone}
                </a>
              )}
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-[1.2rem] bg-white border border-[#1C4358]/10 shadow-md shadow-[#1C4358]/10 p-6 sm:p-9">
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#9C1E22]/10 text-[#9C1E22]"><Sparkles size={24} /></span>
                  <div>
                    <h3 className="text-xl font-display font-semibold text-[#1C4358]">Request your free consultation</h3>
                    <p className="text-[14px] text-[#4B5563]">About: {l.name} · {label}</p>
                  </div>
                </div>
                <IntakeForm
                  cta={l.cta_primary_label || "Book a Free 1-Hour Consultation"}
                  source="local_landing_page"
                  campaign={`local:${data.franchise}:${lane}`}
                  franchise={data.franchise}
                  extra={{
                    matched_service_lane: data.attribution.service_lane,
                    country_id: local.country_id, state_province_id: local.state_province_id,
                    metro_region_id: local.metro_region_id, city_id: local.city_id,
                    service_area_id: local.service_area_id, local_path: data.attribution.local_path,
                  }}
                  defaultService={l.service_interest || ""}
                  submitLabel="Request my free consultation"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
